import { SignInFlowCalloutBlock } from '@gik/auth/components/SignInFlow/SignInFlowCallout';
import { SignInFlowContentCopyVariant } from '@gik/auth/components/SignInFlow/SignInFlowStartContent';
import { signin } from '@gik/auth/utils/auth';
import { useUserStore } from '@gik/core/store/UserStore';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { useInkindStore } from '@gik/inkind-page/store/InkindStore';
import type { StreamParticipant } from '@gik/stream/types/StreamParticipant';
import React from 'react';
import { UserAvatar } from '../../../ui/gik/UserAvatar';
import type { PostInputPayload, PostInputProps } from './PostInput';
import type { StreamInputProps } from './StreamInput';
import { StreamInput } from './StreamInput';
import { openPostInputModal } from './StreamModals';

type StreamWrapperProps = {
  onClick?: () => void;
};

export type StreamInputWithAvatarProps = {
  inkindRouteId: string;
  postModal?: boolean;
  participants?: StreamParticipant[];
  onSuccess?: () => void;
  onSubmit?: (payload: PostInputPayload) => void;
} & StreamInputProps;

function StreamInputWithAvatarComp({
  className,
  inkindRouteId,
  postModal,
  participants: users,
  onSuccess,
  onSubmit,
  ...otherProps
}: React.PropsWithChildren<StreamInputWithAvatarProps>): React.ReactElement {
  const bem = useBemCN('stream-input-avatar');

  const userId = useUserStore(state => state.id);
  const routeId = useInkindStore(store => store.inkindRouteId);
  const wrapperProps: StreamWrapperProps = {};

  const openInputModal = React.useCallback(
    async (inputProps?: Partial<PostInputProps>) => {
      if (!userId) {
        const userId = await signin({
          copyVariant: !routeId ? SignInFlowContentCopyVariant.GENERAL : SignInFlowContentCopyVariant.INKIND_PAGE,
          callout: SignInFlowCalloutBlock.MESSAGE_OR_COMMENT_ATTEMPT,
          routeId,
        });
        if (!userId) {
          return;
        }
      }
      return openPostInputModal(
        { inkindRouteId },
        {
          ...inputProps,
          onSubmit: (payload: PostInputPayload, close) => {
            close();
            return onSubmit(payload);
          },
          users,
        },
        {
          onClose: (response: boolean) => {
            if (response === true) {
              onSuccess?.();
            }
            return response;
          },
        }
      );
    },
    [inkindRouteId, onSubmit, onSuccess, userId, users, routeId]
  );

  if (postModal) {
    wrapperProps.onClick = () => openInputModal();
  }

  const handleImageUploadClick = React.useCallback(() => {
    return openInputModal({ attachOnOpen: true, users });
  }, [openInputModal, users]);

  const handleSelectBg = React.useCallback(() => {
    return openInputModal({ backgroundColor: 'aqua', users });
  }, [openInputModal, users]);

  return (
    <div {...bem(null, [{ clickable: postModal }], className)}>
      <UserAvatar />
      <div {...bem('input-wrapper')} {...wrapperProps}>
        <StreamInput
          {...otherProps}
          preview={postModal}
          onImageUpload={handleImageUploadClick}
          onSelectBg={handleSelectBg}
        />
      </div>
    </div>
  );
}

export const StreamInputWithAvatar = withComponentErrorBoundary(StreamInputWithAvatarComp);
